import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Building2, Database, Info } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import type { CustomerPartitionCheckResult, CustomerPartitionUsage } from "@nexus/contracts";

export interface NewOrgReviewOrganization {
  name: string;
  logoLabel: string;
  accentLabel: string;
  productLineLabel: string;
  industries: string[];
  defaultIndustry: string | null;
  marketsSummary: string;
  defaultBrand: string | null;
}

export type NewOrgReviewOnboarding =
  | {
      mode: "create";
      customerPartition: string;
      compKey: string;
      clientIndustry: string;
      metaAccountIds: string[];
      tsStart: string;
      taggingSector: string;
      filledFieldCount: number;
    }
  | {
      mode: "skip";
      customerPartition: string | null;
      /** False while the deployment has onboarding writes off: Nexus only is the sole option, not a choice. */
      isOnboardingAvailable: boolean;
    };

/** The data-sync read of BigQuery and the Nexus onboarding copy. `unverified` (failed or timed out) is never "does not exist". */
export type NewOrgExistingCustomer =
  | { status: "pending" }
  | { status: "unverified" }
  | { status: "found"; result: CustomerPartitionCheckResult };

/** What the pre-create check knows: Nexus usage answers at once, the existing-customer read follows. */
export type NewOrgPartitionCheck =
  | { status: "none" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; usage: CustomerPartitionUsage; existing: NewOrgExistingCustomer };

interface NewOrgReviewStepProps {
  organization: NewOrgReviewOrganization;
  onboarding: NewOrgReviewOnboarding;
  partitionCheck: NewOrgPartitionCheck;
  /** Create the Nexus organization only, keeping the entered customer partition and writing nothing to BigQuery. */
  onSwitchToNexusOnly: () => void;
}

/**
 * The last step before anything is created. It states exactly what will be
 * saved — the organization plus client onboarding, or the organization only —
 * and surfaces a customer partition another organization already uses.
 */
export function NewOrgReviewStep({ organization, onboarding, partitionCheck, onSwitchToNexusOnly }: NewOrgReviewStepProps) {
  const { t } = useTranslation();
  const none = t("iam.shared.none", "None");
  const usedBy = partitionCheck.status === "ready" ? partitionCheck.usage.usedBy : [];
  const existing = partitionCheck.status === "ready" ? partitionCheck.existing : null;
  const found = existing?.status === "found" ? existing.result : null;
  const existingCustomer = found && (found.existsInBigQuery || found.existsInNexusOnboarding) ? found : null;
  const isUnverified = partitionCheck.status === "error" || existing?.status === "unverified";
  const isChecking = partitionCheck.status === "loading" || existing?.status === "pending";
  const isCreateMode = onboarding.mode === "create";

  return (
    <div className="flex flex-col gap-6" data-testid="new-org-review" role="status">
      <div className="flex flex-col gap-1">
        <h4 className="text-[15px] font-semibold text-foreground">
          {t("iam.newOrgDialog.review.title", "Review before creating")}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t("iam.newOrgDialog.review.nothingYet", "Nothing is saved until you confirm.")}
        </p>
      </div>

      <div
        className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-4 py-3"
        data-testid={`new-org-review-mode-${onboarding.mode}`}
      >
        {onboarding.mode === "create" ? (
          <Database className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        ) : (
          <Building2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        )}
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">
            {onboarding.mode === "create"
              ? t("iam.newOrgDialog.review.modeCreate", "Organization + customer data")
              : t("iam.newOrgDialog.review.modeSkip", "Nexus organization only")}
          </p>
          <p className="text-xs text-muted-foreground">
            {onboarding.mode === "create"
              ? t(
                  "iam.newOrgDialog.review.modeCreateHint",
                  "Creates the organization in Nexus, then creates the customer's onboarding records: brand context and data setup. Nexus keeps its copy once they are confirmed.",
                )
              : onboarding.isOnboardingAvailable
                ? t(
                    "iam.newOrgDialog.review.modeSkipHint",
                    "Only the Nexus organization is created. The customer partition, if you entered one, is kept on it. No customer data is set up; you can do that later from Edit organization.",
                  )
                : t(
                    "iam.newOrgDialog.review.modeNexusOnlyHint",
                    "Creates the organization in Nexus only. Client onboarding can't be created from Nexus yet, so nothing is written to BigQuery and Nexus keeps no onboarding record. Set the Customer partition later on the Organization tab.",
                  )}
          </p>
        </div>
      </div>

      {partitionCheck.status === "ready" && usedBy.length > 0 && (
        <div
          className="flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3"
          role="alert"
          data-testid="new-org-review-partition-used"
        >
          <p className="flex items-start gap-2 text-sm text-warning-subtle-foreground">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t("iam.newOrgDialog.review.partitionUsed", {
              names: usedBy.map((tenant) => tenant.name).join(", "),
              defaultValue: "A customer partition already exists for: {{names}}",
            })}
          </p>
          <p className="text-xs text-warning-subtle-foreground">
            {t(
              "iam.newOrgDialog.review.partitionUsedHint",
              "Two Nexus organizations can't share a customer partition, so creating with it is refused and nothing is created or changed. Go back and change the partition.",
            )}
          </p>
        </div>
      )}
      {isCreateMode && isChecking && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="new-org-review-checking">
          <Info className="size-3.5 shrink-0" aria-hidden />
          {t("iam.newOrgDialog.review.checking", "Checking whether this customer already exists…")}
        </p>
      )}
      {existingCustomer && (
        <div
          className="flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3"
          role="alert"
          data-testid="new-org-review-customer-exists"
        >
          <p className="flex items-start gap-2 text-sm text-warning-subtle-foreground">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t("iam.newOrgDialog.review.customerExists", {
              name: existingCustomer.customerName,
              defaultValue: "A customer already exists for: {{name}}",
            })}
          </p>
          <p className="text-xs text-warning-subtle-foreground">
            {isCreateMode
              ? t(
                  "iam.newOrgDialog.review.customerExistsHint",
                  "Creating onboarding would collide with it, so it is not allowed. Choose Nexus only to create the organization here and keep this customer partition, without creating or changing any onboarding or customer data in BigQuery.",
                )
              : t(
                  "iam.newOrgDialog.review.customerExistsNexusOnlyHint",
                  "Nexus only keeps this customer partition on the new organization and does not create or change any onboarding or customer data in BigQuery.",
                )}
          </p>
          {isCreateMode && (
            <div>
              <Button type="button" size="sm" variant="outline" onClick={onSwitchToNexusOnly} data-testid="new-org-review-switch-nexus-only">
                {t("iam.newOrgDialog.review.switchToNexusOnly", "Switch to Nexus only")}
              </Button>
            </div>
          )}
        </div>
      )}
      {isCreateMode && isUnverified && (
        <div
          className="flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3"
          role="alert"
          data-testid="new-org-review-unverified"
        >
          <p className="flex items-start gap-2 text-sm text-warning-subtle-foreground">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t("iam.newOrgDialog.review.unverified", "Couldn't verify whether this customer already exists.")}
          </p>
          <p className="text-xs text-warning-subtle-foreground">
            {t(
              "iam.newOrgDialog.review.unverifiedHint",
              "Without that check the full onboarding can't be created. Choose Nexus only to create the organization and keep the customer partition without writing anything to BigQuery.",
            )}
          </p>
          <div>
            <Button type="button" size="sm" variant="outline" onClick={onSwitchToNexusOnly} data-testid="new-org-review-switch-nexus-only">
              {t("iam.newOrgDialog.review.switchToNexusOnly", "Switch to Nexus only")}
            </Button>
          </div>
        </div>
      )}

      <ReviewSection title={t("iam.newOrgDialog.review.organization", "Organization")}>
        <ReviewRow label={t("iam.newOrgDialog.displayNameLabel", "Display name")} value={organization.name} />
        <ReviewRow label={t("iam.newOrgDialog.logoLabel", "Logo")} value={organization.logoLabel} />
        <ReviewRow label={t("iam.newOrgDialog.review.accent", "Accent color")} value={organization.accentLabel} />
        <ReviewRow label={t("iam.newOrgDialog.productLineLabel", "Product line")} value={organization.productLineLabel} />
        <ReviewRow
          label={t("iam.newOrgDialog.industriesLabel", "Industries")}
          value={organization.industries.length > 0 ? organization.industries.join(", ") : none}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.review.defaultIndustry", "Default industry")}
          value={organization.defaultIndustry ?? none}
        />
        <ReviewRow label={t("iam.newOrgDialog.marketsLabel", "Pre Flight markets")} value={organization.marketsSummary} />
        <ReviewRow label={t("iam.newOrgDialog.defaultBrandLabel", "Default brand")} value={organization.defaultBrand ?? none} />
      </ReviewSection>

      <ReviewSection title={t("iam.newOrgDialog.review.onboarding", "Customer data")}>
        {onboarding.mode === "create" ? (
          <>
            <ReviewRow
              label={t("iam.orgDrawer.settings.customerPartitionLabel", "Customer partition")}
              value={onboarding.customerPartition}
              isFlagged={usedBy.length > 0}
            />
            <ReviewRow label={t("iam.newOrgDialog.onboarding.compKeyLabel", "Competitor partition")} value={onboarding.compKey} />
            <ReviewRow label={t("iam.onboarding.fields.industry", "Client industry")} value={onboarding.clientIndustry} />
            <ReviewRow
              label={t("iam.onboarding.setup.fields.account_id", "Meta Account ID")}
              value={onboarding.metaAccountIds.join(", ")}
            />
            <ReviewRow label={t("iam.onboarding.setup.fields.ts_start", "TS start")} value={onboarding.tsStart} />
            <ReviewRow label={t("iam.onboarding.setup.fields.tagging_sector", "Tagging sector")} value={onboarding.taggingSector} />
            <ReviewRow
              label={t("iam.newOrgDialog.review.onboardingFields", "Onboarding fields filled")}
              value={String(onboarding.filledFieldCount)}
            />
          </>
        ) : (
          <>
            <ReviewRow
              label={t("iam.orgDrawer.settings.customerPartitionLabel", "Customer partition")}
              value={
                onboarding.customerPartition
                  ? `${onboarding.customerPartition} — ${t("iam.newOrgDialog.review.partitionKept", "Kept on the organization in Nexus only")}`
                  : t("iam.newOrgDialog.review.partitionNotSet", "Not set")
              }
              isFlagged={usedBy.length > 0}
            />
            <ReviewRow
              label={t("iam.newOrgDialog.review.onboarding", "Customer data")}
              value={t("iam.newOrgDialog.review.onboardingSkipped", "Not set up — no customer data is created")}
            />
          </>
        )}
      </ReviewSection>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h5 className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">{title}</h5>
      <dl className="grid grid-cols-[minmax(160px,auto)_1fr] gap-x-4 gap-y-1.5 rounded-lg border border-border-subtle p-4">{children}</dl>
    </section>
  );
}

function ReviewRow({ label, value, isFlagged = false }: { label: string; value: string; isFlagged?: boolean }) {
  return (
    <>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("min-w-0 break-words text-xs text-foreground", isFlagged && "font-medium text-warning-subtle-foreground")}>
        {value}
      </dd>
    </>
  );
}
